import React, { MouseEvent, useMemo, useState } from "react";
import { Box, Button, Typography, Menu, MenuItem } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { Version } from "../../../types/version";
import VersionTeamTable from "../../../components/tables/VersionTeamTable";
import {
  SYSTEM_TEAM_CONTRIBUTION_TYPE_LABELS,
  SystemTeamContributionTypes,
  VersionTeamConnection,
} from "../../../types/versionTeam";
import useVersionTeamConnectionController from "../../../controllers/connections/VersionTeamConnectionController";
import { languageBasedTypes } from "../../../hooks/logic/connections/versionTeamConnectionLogic";

interface VersionTeamSectionProps {
  anchor: Version;
  systemLanguageType: VersionTeamConnection["system_language_type"];
}

const VersionTeamSection: React.FC<VersionTeamSectionProps> = ({
  anchor,
  systemLanguageType,
}) => {
  const filteredTypes = useMemo<SystemTeamContributionTypes[]>(
    () => languageBasedTypes(systemLanguageType),
    [systemLanguageType]
  );

  const [selectedType, setSelectedType] = useState<SystemTeamContributionTypes>(
    filteredTypes[0]
  );

  const controller = useVersionTeamConnectionController({
    from: anchor,
    versionTeamType: selectedType,
    systemLanguageType,
  });

  const { openCreateDialog } = controller;

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const openMenu = (e: MouseEvent<HTMLElement>) => setAnchorEl(e.currentTarget);
  const closeMenu = () => setAnchorEl(null);

  const handleSelect = (key: SystemTeamContributionTypes) => {
    setSelectedType(key);
    closeMenu();
    openCreateDialog();
  };

  return (
    <Box sx={{ width: "100%", mb: 4 }}>
      <Box sx={{ position: "relative", mb: 2, textAlign: "center" }}>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={openMenu}
          sx={{ position: "absolute", left: 16 }}
        >
          New Version Team
        </Button>
        <Typography variant="h2">Version Teams</Typography>
        <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={closeMenu}>
          {filteredTypes.map((type) => (
            <MenuItem key={type} onClick={() => handleSelect(type)}>
              {SYSTEM_TEAM_CONTRIBUTION_TYPE_LABELS[type]}
            </MenuItem>
          ))}
        </Menu>
      </Box>
      <VersionTeamTable
        controller={controller}
        versionTeamType={selectedType}
        systemLanguageType={systemLanguageType}
      />
    </Box>
  );
};

export default VersionTeamSection;
